/**
 * B26 slice 2 — the full §12 integration catalog (spec §12: "domain events
 * are the integration currency: OrderConfirmed, OrderExpired,
 * PaymentSucceeded, RefundApplied, EntitlementIssued, EntitlementVoided,
 * EntitlementExpired, RedemptionRecorded, SlotCancelled,
 * AllocationReleased, CapacityLow, plus period ticks (day/month close) for
 * the accounting module (§10.5)").
 *
 * M1 transitions emit machine-local event types; this module CLASSIFIES
 * that vocabulary — each type either crosses the membrane under its §12
 * name or is explicitly internal. The classification is total and
 * fail-closed: an unclassified type throws, so a new M1 event cannot
 * silently leak (or silently vanish).
 *
 * Decisions (B26, 2026-07-13):
 * - Period ticks close over UTC boundaries in Phase 1; the tenant-timezone
 *   variant rides with tenant config (B27/B31). Ticks carry deterministic
 *   ids (`tick-day-YYYY-MM-DD`, `tick-month-YYYY-MM`) so the outbox's
 *   put-if-absent makes scheduler re-runs (B29) idempotent.
 * - CapacityLow derives from pool state at write time and exposes
 *   AVAILABLE UNITS ONLY (§4.3: never raw sold/held). Deterministic id per
 *   (pool, capacity, threshold): one signal per configured capacity per
 *   threshold — re-fires when capacity is re-configured, not on every sale.
 * - RefundApplied is the ORDER-level fact (order.refund_applied); the
 *   intent-level echo (payment.refund_applied) stays internal.
 * - RedemptionRecorded is entitlement.redeemed; reversal/exhaustion are
 *   §8-internal mechanics until a consumer needs them.
 */

import { available, type CapacityPool } from "./capacity.js";
import { InvalidEventEnvelopeError, toEnvelope, type EmittedEvent, type EventEnvelope } from "./events.js";

/* ------------------------------------------------------------ vocabulary */

/** The §12 integration currency, plus the §10.5 period ticks. */
export const INTEGRATION_EVENT_NAMES = [
  "OrderConfirmed",
  "OrderExpired",
  "PaymentSucceeded",
  "RefundApplied",
  "EntitlementIssued",
  "EntitlementVoided",
  "EntitlementExpired",
  "RedemptionRecorded",
  "SlotCancelled",
  "AllocationReleased",
  "CapacityLow",
  "PeriodTickDay",
  "PeriodTickMonth",
] as const;

export type IntegrationEventName = (typeof INTEGRATION_EVENT_NAMES)[number];

export class UnknownDomainEventError extends Error {
  constructor(type: string) {
    super(`Domain event type "${type}" is not classified in the §12 catalog — classify it (integration or internal)`);
    this.name = "UnknownDomainEventError";
  }
}

/**
 * Total classification of the M1 vocabulary. "internal" = a state-machine
 * step that is not integration currency (consumers see its consequences
 * through the events that are).
 */
export const M1_EVENT_CLASSIFICATION: Readonly<Record<string, IntegrationEventName | "internal">> = Object.freeze({
  // Capabilities (§2.3, B40) — definition lifecycle is operator machinery,
  // not integration currency: a run's consequences cross through the events
  // its steps themselves emit.
  "capability.activated": "internal",
  "capability.paused": "internal",
  "capability.resumed": "internal",
  "capability.retired": "internal",
  // Orders (§5.1)
  "order.checked_out": "internal",
  "order.confirmed": "OrderConfirmed",
  "order.expired": "OrderExpired",
  "order.cancelled": "internal",
  "order.refund_applied": "RefundApplied",
  // The terminal tranche (refund_remaining → REFUNDED) is still money
  // applied — consumers of RefundApplied must see EVERY tranche, including
  // the one that completes the refund (found by the B26 write-path tests).
  "order.refunded": "RefundApplied",
  // Payments (§5.3) — the intent machine's local steps stay local.
  "payment.processing": "internal",
  "payment.succeeded": "PaymentSucceeded",
  "payment.failed": "internal",
  "payment.cancelled": "internal",
  "payment.refund_applied": "internal",
  "payment.refunded": "internal",
  // Entitlements (§5.2)
  "entitlement.issued": "EntitlementIssued",
  "entitlement.activated": "internal",
  "entitlement.redeemed": "RedemptionRecorded",
  "entitlement.redemption_reversed": "internal",
  "entitlement.exhausted": "internal",
  "entitlement.expired": "EntitlementExpired",
  "entitlement.voided": "EntitlementVoided",
  // Slots (§5.4)
  "slot.published": "internal",
  "slot.closed": "internal",
  "slot.reopened": "internal",
  "slot.completed": "internal",
  "slot.cancelled": "SlotCancelled",
  // Allocations (§6)
  AllocationReleased: "AllocationReleased",
});

/** Classify one M1 type; fail-closed for strangers. */
export function classifyDomainEvent(type: string): IntegrationEventName | "internal" {
  const name = M1_EVENT_CLASSIFICATION[type];
  if (name === undefined) throw new UnknownDomainEventError(type);
  return name;
}

/**
 * Lift an M1 event into its §12 envelope: the integration name outside,
 * the full original payload (plus sourceType) inside. Internal events
 * return undefined — they never enter the outbox for the bus.
 */
export function toIntegrationEnvelope(event: EmittedEvent, eventId: string): EventEnvelope | undefined {
  const name = classifyDomainEvent(event.type);
  if (name === "internal") return undefined;
  return toEnvelope({ ...event, type: name, sourceType: event.type }, eventId);
}

/* ----------------------------------------------------------- period ticks */

export interface PeriodTickRange {
  readonly tenantId: string;
  /** Ticks strictly after this instant… */
  readonly fromExclusive: number;
  /** …up to and including this one. */
  readonly toInclusive: number;
}

const DAY_MS = 86_400_000;

const pad = (n: number, w: number): string => String(n).padStart(w, "0");

/**
 * §10.5's triggers: one PeriodTickDay per UTC day close in the range, one
 * PeriodTickMonth per UTC month close. Deterministic ids make re-runs
 * outbox-idempotent; occurredAt is the boundary instant itself.
 */
export function periodTicks(range: PeriodTickRange): readonly EventEnvelope[] {
  if (range.tenantId.length === 0) throw new RangeError("periodTicks requires a tenantId");
  if (!Number.isSafeInteger(range.fromExclusive) || !Number.isSafeInteger(range.toInclusive)) {
    throw new RangeError("periodTicks requires epoch-millis bounds");
  }
  const out: EventEnvelope[] = [];
  // First UTC midnight strictly after fromExclusive.
  let boundary = Math.floor(range.fromExclusive / DAY_MS) * DAY_MS + DAY_MS;
  for (; boundary <= range.toInclusive; boundary += DAY_MS) {
    const closed = new Date(boundary - DAY_MS);
    const day = `${closed.getUTCFullYear()}-${pad(closed.getUTCMonth() + 1, 2)}-${pad(closed.getUTCDate(), 2)}`;
    out.push(
      toEnvelope(
        { type: "PeriodTickDay", tenantId: range.tenantId, at: boundary, day },
        `tick-day-${day}`,
      ),
    );
    const atBoundary = new Date(boundary);
    if (atBoundary.getUTCDate() === 1) {
      const month = `${closed.getUTCFullYear()}-${pad(closed.getUTCMonth() + 1, 2)}`;
      out.push(
        toEnvelope(
          { type: "PeriodTickMonth", tenantId: range.tenantId, at: boundary, month },
          `tick-month-${month}`,
        ),
      );
    }
  }
  return Object.freeze(out);
}

/* ------------------------------------------------------------ CapacityLow */

export interface CapacityLowOptions {
  /** Fire at or under this share of capacity remaining, in basis points. */
  readonly thresholdBps: number;
  /** Override the deterministic id (tests, custom dedupe windows). */
  readonly eventId?: string;
}

/**
 * The §12 operational signal, derived from pool state the writer already
 * holds. Exposes available units only — never raw sold/held (§4.3). Fires
 * iff available·10000 ≤ capacity·thresholdBps (a zero-capacity pool is
 * always at zero availability).
 */
export function capacityLowSignal(
  pool: CapacityPool,
  at: number,
  opts: CapacityLowOptions,
): EventEnvelope | undefined {
  if (!Number.isInteger(opts.thresholdBps) || opts.thresholdBps < 0 || opts.thresholdBps > 10_000) {
    throw new RangeError(`thresholdBps must be an integer in 0..10000, got ${opts.thresholdBps}`);
  }
  const availableUnits = available(pool);
  if (availableUnits * 10_000 > pool.capacity * opts.thresholdBps) return undefined;
  return toEnvelope(
    {
      type: "CapacityLow",
      tenantId: pool.tenantId,
      at,
      poolId: pool.poolId,
      availableUnits,
      capacity: pool.capacity,
      thresholdBps: opts.thresholdBps,
    },
    opts.eventId ?? `caplow-${pool.poolId}-${pool.capacity}-${opts.thresholdBps}`,
  );
}

/* ------------------------------------------------------ write-path emission */

/**
 * B26 write-path emission: deterministic event ids. The same state change
 * must mint the SAME id on every retry, so the outbox's put-if-absent
 * turns at-least-once handler execution into exactly-once bus events.
 */

/** Types that can legitimately recur for one natural key — id needs a discriminator. */
const REQUIRES_DISCRIMINATOR: ReadonlySet<IntegrationEventName> = new Set(["RefundApplied", "RedemptionRecorded"]);

/** Natural-key fields in specificity order (intentId before orderId: payment events carry both). */
const NATURAL_KEYS = ["entitlementId", "intentId", "allocationId", "slotId", "orderId"] as const;

export class MissingEventDiscriminatorError extends Error {
  constructor(name: IntegrationEventName) {
    super(
      `${name} recurs per natural key — its outbox id needs a discriminator (refund id, redemption id, …); refusing to guess`,
    );
    this.name = "MissingEventDiscriminatorError";
  }
}

/**
 * Deterministic outbox id for a crossing event: `evt <IntegrationName> <key>`
 * (+ ` <discriminator>` for repeatable facts). Internal events have no id —
 * classify first. The discriminator is IGNORED for once-only facts so the id
 * never depends on the call site.
 */
/** What a concrete M1 event type must carry to be emittable (no index signature required). */
export interface EmittableEvent {
  readonly type: string;
  readonly tenantId: string;
  readonly at: number;
}

export function integrationEventId(event: EmittableEvent, discriminator?: string): string {
  return integrationEventIdOf(event as EmittedEvent, discriminator);
}

function integrationEventIdOf(event: EmittedEvent, discriminator?: string): string {
  const name = classifyDomainEvent(event.type);
  if (name === "internal") {
    throw new UnknownDomainEventError(`${event.type} is internal — internal events never enter the outbox`);
  }
  const keyField = NATURAL_KEYS.find((f) => typeof event[f] === "string" && (event[f] as string).length > 0);
  if (keyField === undefined) {
    throw new InvalidEventEnvelopeError(`event ${event.type} carries no natural key (${NATURAL_KEYS.join(", ")})`);
  }
  const base = `evt ${name} ${event[keyField] as string}`;
  if (!REQUIRES_DISCRIMINATOR.has(name)) return base;
  if (discriminator === undefined || discriminator.length === 0) throw new MissingEventDiscriminatorError(name);
  return `${base} ${discriminator}`;
}

/**
 * The one call handlers make: M1 transition events in, §12 envelopes out.
 * Internal events are silence; crossing events get their deterministic id.
 * Pass the write's discriminator (refund id, redemption id) when the batch
 * may contain repeatable facts.
 */
export function integrationEnvelopesFrom(
  events: readonly EmittableEvent[],
  discriminator?: string,
): readonly EventEnvelope[] {
  const out: EventEnvelope[] = [];
  for (const raw of events) {
    const event = raw as EmittedEvent;
    if (classifyDomainEvent(event.type) === "internal") continue;
    out.push(toIntegrationEnvelope(event, integrationEventIdOf(event, discriminator))!);
  }
  return Object.freeze(out);
}

/* ---------------------------------------------------------------- parsing */

export interface ParsedIntegrationEventId {
  readonly name: IntegrationEventName;
  readonly naturalKey: string;
  readonly discriminator?: string;
}

export class MalformedIntegrationEventIdError extends Error {
  constructor(eventId: string, why: string) {
    super(`Not a minted integration event id (${why}): ${JSON.stringify(eventId)}`);
    this.name = "MalformedIntegrationEventIdError";
  }
}

const INTEGRATION_NAME_SET: ReadonlySet<string> = new Set(INTEGRATION_EVENT_NAMES);

/**
 * Invert `integrationEventId`: `evt <Name> <key>[ <discriminator>]`. The
 * format is this module's private knowledge — consumers that need the
 * discriminator (B27's projector hydrating a refund row) parse HERE, never
 * with their own string surgery. Fail-closed: anything the minter could
 * not have produced throws.
 */
export function parseIntegrationEventId(eventId: string): ParsedIntegrationEventId {
  const parts = eventId.split(" ");
  if (parts.length < 3 || parts[0] !== "evt") {
    throw new MalformedIntegrationEventIdError(eventId, "expected `evt <Name> <key>`");
  }
  const [, name, naturalKey, ...rest] = parts;
  if (!INTEGRATION_NAME_SET.has(name!)) {
    throw new MalformedIntegrationEventIdError(eventId, `unknown integration name ${JSON.stringify(name)}`);
  }
  if (naturalKey!.length === 0) throw new MalformedIntegrationEventIdError(eventId, "empty natural key");
  const discriminator = rest.join(" ");
  return Object.freeze({
    name: name as IntegrationEventName,
    naturalKey: naturalKey!,
    ...(discriminator.length > 0 ? { discriminator } : {}),
  });
}
